import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CustomModal, FiltersButton, InfoBanner, Spinner, TableHeader, WithRoleAccess } from "@services/ui-components";
import { Button, Col, Container, Row } from "react-bootstrap";
import { CategoryPickerCategory, CategoryPickerComponent } from "@me-pos/category-picker";
import { CategoryInterface } from "../../../services/ServicesCategoryInnit";
import TableServices from "./TableServices";
import { requirementsInfoType } from "../../../interfaces/interfaces";
import RequirementsFileInfo from "../../../services/importFiles/RequirementsFileInfo";
import i18next from "i18next";
import { ImportFiles } from "../../ImportFiles/ImportFiles";
import { ErrorModalComponent } from "@me-pos/error-modal";
import ErrorService from "../../../services/ErrorService";
import { ExportBtn } from "../../ExportBtn/ExportBtn";
import { EntityType } from "../../../utils/enums";
import transformCategories from "@me-team/host/src/utils/transformCategories";
import { useGetCategoryQuery } from "@me-team/host/main/services/graphql/services.hooks";
import JoyrideWrapper from "../../Joyride/JoyrideWrapper";
import { changeStep } from "../../Joyride/joyrideCallbackHandler";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useCheckServicesQuery } from "@me-team/host/main/tutorial/graphql/tutorial.hooks";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess, useRoleAccessAny } from "@me-team/host/src/utils/useRoleAccess";

interface ServicesLocationState {
    serviceId?: number | null;
    text?: string;
    toast?: boolean;
    service?: number | null;
}

const Services: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const runJoy = localStorage.getItem("run") || false;
    const step = localStorage.getItem("step") || "0";
    const [run] = useState(runJoy);
    const [stepIndex, setStepIndex] = useState<number>(Number(step));
    const { categoryId, subCategoryId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const hasPageActions = useRoleAccessAny([
        CompanyAccess.ServiceExport,
        CompanyAccess.ServiceImport,
        CompanyAccess.ServiceCreate,
    ]);
    const canViewServiceEmployees = useRoleAccess(CompanyAccess.ServiceEmployeesView);
    const newServiceId = location.state?.serviceId;
    const [createdServiceId, setCreatedServiceId] = useState<number | null>(newServiceId);

    const [isSettingsModalShown, setIsSettingsModalShown] = useState(!!createdServiceId);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [showFilters, setShowFilters] = useState(false);
    const [currentNameFilter, setCurrentNameFilter] = useState({
        categoryName: "",
        subCategoryName: "",
    });

    const [currentIdFilter, setCurrentIdFilter] = useState({ categoryId: null, subCategoryId: null });
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState({
        name: "",
        categoryId: +categoryId || null,
        subCategoryId: +subCategoryId || null,
        id: location?.state?.service,
    });
    const [selectedCategory, setSelectedCategory] = useState<CategoryInterface[]>(null);

    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
    const [serviceRequirementsInfo, setServiceRequirementsInfo] = useState<requirementsInfoType>(null);
    const componentRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [hasExistingServices, setHasExistingServices] = useState<boolean>(false);

    useEffect(() => {
        const state = location.state as ServicesLocationState | null;
        const incomingServiceId = state?.serviceId ?? null;
        if (incomingServiceId != null && createdServiceId == null) {
            setCreatedServiceId(incomingServiceId);
            setIsSettingsModalShown(true);
        }
    }, [location.key, location.state, createdServiceId]);

    useEffect(() => {
        if (currentCompanyId) {
            setServiceRequirementsInfo(
                RequirementsFileInfo.serviceFileRequirements(parseInt(currentCompanyId), t, i18next.language, branchId)
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18next.language, currentCompanyId]);

    const toggleFilters = () => setShowFilters(!showFilters);

    const { loading: checkServicesLoading } = useCheckServicesQuery({
        variables: {
            companyId: Number(currentCompanyId),
        },
        skip: !currentCompanyId || !runJoy,
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            const isServices: boolean = !!data?.user?.company?.servicesList?.length;
            setHasExistingServices(isServices);
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const { data: categoryData, loading } = useGetCategoryQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            setSelectedCategory(transformCategories(data?.user?.company?.serviceCategoriesNew));
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
                setIsPickerOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleCategoryNameChange = (categoryName: string) => {
        setCurrentNameFilter({ categoryName, subCategoryName: "" });
    };

    const onOpenImportModalHandler = () => {
        setIsImportModalOpen(true);
    };
    const onCloseImportModalHandler = () => {
        setIsImportModalOpen(false);
    };

    const handleSubCategoryNameChange = (subCategoryName: string, categoryName: string = "") => {
        setCurrentNameFilter({ categoryName, subCategoryName });
    };

    const handleCategoryIdChange = (categoryId: number) => {
        setCurrentIdFilter({ categoryId, subCategoryId: null });
    };

    const handleSubCategoryIdChange = (categoryId: number, subCategoryId: number) => {
        setCurrentIdFilter((prevState) => ({
            ...prevState,
            categoryId: categoryId,
            subCategoryId: subCategoryId,
        }));
    };

    const handleApplyFilter = () => {
        setFilter((prev) => ({
            ...prev,
            name: "",
            categoryId: null,
            subCategoryId: null,
        }));
        setFilter((prev) => ({
            ...prev,
            categoryId: currentIdFilter?.categoryId,
            subCategoryId: currentIdFilter?.subCategoryId,
            name: search,
        }));

        setIsPickerOpen(false);
    };

    const resetSelectedCategories = (categories: CategoryInterface[]) => {
        return categories.map((category) => ({
            ...category,
            isPicked: false,
            isSubcategoryPiked: false,
            subcategoryList: category.subcategoryList.map((subcategory) => ({
                ...subcategory,
                isPicked: false,
            })),
        }));
    };

    const handleClearFilter = () => {
        setFilter((prev) => ({
            ...prev,
            name: "",
            categoryId: null,
            subCategoryId: null,
        }));
        setCurrentNameFilter({ categoryName: "", subCategoryName: "" });
        setSearch("");
        if (selectedCategory) {
            setSelectedCategory(resetSelectedCategories(selectedCategory));
        }
        setIsPickerOpen(false);
    };

    const handleCreateService = () => {
        run && stepIndex === 10 && changeStep(stepIndex, setStepIndex, "next");
        navigate(`/services/service/${currentCompanyId}/new/${branchId}`);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleApplyFilter();
        }
    };

    if (loading || !currentCompanyId || checkServicesLoading) return <Spinner />;

    return (
        <div className="mb-9">
            {+step === 10 && run && (
                <JoyrideWrapper
                    run={run}
                    stepIndex={stepIndex}
                    setStepIndex={setStepIndex}
                    hasExistingServices={hasExistingServices}
                />
            )}
            <Col>
                <InfoBanner>
                    <p className="fs-7 mb-0">
                        {t("infoBanner")} <i className="bi bi-people"></i>
                    </p>
                </InfoBanner>
            </Col>
            <TableHeader
                title={t("Service list")}
                paths={[
                    { path: `/services/service/${currentCompanyId}/service-list/${branchId}`, label: t("services") },
                    {
                        path: `/services/service/${currentCompanyId}/service-list/${branchId}`,
                        label: t("Service list"),
                    },
                ]}
                colSize={hasPageActions ? 4 : 6}
                btnColSize={hasPageActions ? 8 : 6}
                buttons={
                    <Row className="gx-3 flex-nowrap justify-content-end mt-3 mt-lg-0">
                        <WithRoleAccess permission={CompanyAccess.ServiceExport}>
                            <Col xs={2} xl={3} xxl={3}>
                                <ExportBtn
                                    companyId={parseInt(currentCompanyId)}
                                    exportType={EntityType.SERVICES}
                                    branchId={branchId}
                                    classes="d-none d-xl-inline-block"
                                />
                            </Col>
                        </WithRoleAccess>
                        <WithRoleAccess permission={CompanyAccess.ServiceImport}>
                            <Col xs={2} xl={3} xxl={3}>
                                <Button
                                    variant="outline-violet"
                                    className="border-primary text-primary fw-normal d-flex align-items-center justify-content-center w-100 mb-3 mb-lg-0"
                                    onClick={onOpenImportModalHandler}
                                >
                                    <i className="bi bi-box-arrow-in-down"></i>
                                    <span className="d-none d-xl-inline-block ms-1">{t("import")}</span>
                                </Button>
                            </Col>
                        </WithRoleAccess>
                        <WithRoleAccess permission={CompanyAccess.ServiceCreate}>
                            <Col xs={6} xl={4} xxl={4}>
                                <Button
                                    variant="primary"
                                    className={`w-100 mb-3 mb-lg-0 text-truncate fw-bold ${stepIndex === 10 ? "provide-services" : ""}`}
                                    onClick={handleCreateService}
                                >
                                    <i className="bi bi-plus-lg fw-bold me-1 w-100"></i>
                                    {t("Add a service")}
                                </Button>
                            </Col>
                        </WithRoleAccess>
                        <Col xs={hasPageActions ? 2 : 5} xl={hasPageActions ? 2 : 3} xxl={2}>
                            <FiltersButton
                                filter={filter}
                                showFilters={showFilters}
                                onClick={toggleFilters}
                                classesText={`${hasPageActions ? "d-none d-xxl-inline-block" : "d-inline-block"}`}
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
            >
                <Row className="pb-6">
                    <Col lg={8} xl={9}>
                        <Row className="gx-3">
                            <Col sm={6} className="text-primary">
                                <div className="position-relative mb-3 mb-lg-0">
                                    <input
                                        id="serviceList-search-field"
                                        type="text"
                                        className="form-control pe-4"
                                        placeholder={t("Search by service name")}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        onKeyDown={handleSearchKeyDown}
                                    />
                                    <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y text-dark me-2"></i>
                                </div>
                            </Col>
                            <Col sm={6}>
                                <div className="position-relative mb-3 mb-lg-0" ref={componentRef}>
                                    <input
                                        type="text"
                                        id="serviceList-category-field"
                                        className="form-control px-6 pe-4 cursor-pointer"
                                        placeholder={t("category")}
                                        ref={inputRef}
                                        value={
                                            currentNameFilter?.categoryName || currentNameFilter?.subCategoryName
                                                ? `${currentNameFilter?.categoryName}  ${currentNameFilter?.subCategoryName}`
                                                : ""
                                        }
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsPickerOpen((prev) => !prev);
                                        }}
                                        readOnly
                                    />
                                    <i
                                        className="bi bi-chevron-down position-absolute top-50 end-0 translate-middle-y text-dark me-2"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsPickerOpen(!isPickerOpen);
                                            inputRef.current?.focus();
                                        }}
                                    ></i>
                                    <div className="dropdown position-absolute opacity-1 z-2 top-10  start-0 end-0 mt-2 bg-white shadow">
                                        {categoryData && selectedCategory && (
                                            <CategoryPickerComponent
                                                i18n={i18next}
                                                isOpen={isPickerOpen}
                                                onSearchChange={() => {}}
                                                onChangeCategory={(currentCategory: CategoryPickerCategory) => {
                                                    if (currentCategory.isPicked) {
                                                        handleCategoryNameChange(currentCategory?.name);
                                                        handleCategoryIdChange(currentCategory?.id);
                                                        setIsPickerOpen(false);
                                                    } else {
                                                        setCurrentNameFilter({ categoryName: "", subCategoryName: "" });
                                                    }
                                                }}
                                                onChangeSubcategory={(currentSubcategory, currentCategory) => {
                                                    if (currentSubcategory.isPicked) {
                                                        handleSubCategoryNameChange(currentSubcategory?.name);
                                                        handleSubCategoryIdChange(
                                                            currentCategory?.id,
                                                            currentSubcategory?.id
                                                        );
                                                        setIsPickerOpen(false);
                                                    } else {
                                                        setCurrentNameFilter({ categoryName: "", subCategoryName: "" });
                                                    }
                                                }}
                                                serviceCategories={selectedCategory}
                                            />
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                    <Col lg={4} xl={3}>
                        <Row className="gx-3 h-100">
                            <Col xs={6} className="mb-3 mb-lg-0">
                                <Button
                                    variant="outline-dark"
                                    type="button"
                                    className="border-grey w-100 h-100  fw-normal"
                                    onClick={handleClearFilter}
                                >
                                    {t("clear")}
                                </Button>
                            </Col>
                            <Col xs={6} className="mb-3 mb-lg-0">
                                <Button variant="primary" className="w-100 h-100 px-1" onClick={handleApplyFilter}>
                                    {t("apply")}
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </TableHeader>
            <TableServices filter={filter} />
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
            {serviceRequirementsInfo && (
                <ImportFiles
                    currentRequirementsInfo={serviceRequirementsInfo}
                    isOpen={isImportModalOpen}
                    onClose={onCloseImportModalHandler}
                />
            )}
            <CustomModal
                show={isSettingsModalShown}
                handleClose={() => setIsSettingsModalShown(false)}
                title={
                    <Container>
                        <Row>
                            <h5 className="m-0 p-0 fw-normal">{t("Service created!")}</h5>
                        </Row>
                    </Container>
                }
                body={
                    <Container>
                        <Row>
                            <Col>
                                <span className="fs-6 me-1">
                                    {t("Do you want to configure the service providers for this service?")}
                                </span>
                            </Col>
                        </Row>
                    </Container>
                }
                buttons={[
                    {
                        text: t("Later"),
                        onClick: () => setIsSettingsModalShown(false),
                        variant: "outline-primary",
                        className: "w-100 fw-normal",
                    },
                    ...(canViewServiceEmployees
                        ? [
                              {
                                  text: t("Go to settings"),
                                  onClick: () => navigate(`/services-providers/${createdServiceId}/${branchId}`),
                                  variant: "primary",
                                  className: "w-100 fw-normal",
                              },
                          ]
                        : []),
                ]}
            />
        </div>
    );
};

export default Services;
